import { useMemo } from 'react'
import {
  isEmpty,
  isTeamId,
  toTeamId,
  unique,
  type EntityId,
  type PermissionType,
  type TeamData,
  type TeamId,
  type UserData,
  type UserId,
} from '@terros/common'
import { useProfile, type ProfileState } from '../user/get'
// Import specific functions instead of the whole module to avoid circular dependencies
import { useTeams } from '../team/list'

type Options = {
  permissionType: PermissionType
  userId?: UserId
  includeArchivedTeams?: boolean
}

export const usePermissionMap = ({ userId, permissionType, includeArchivedTeams = false }: Options): EntityId[] => {
  const profile = useProfile(userId)
  const { data: teams } = useTeams(includeArchivedTeams ? { showArchived: 'all' } : undefined)
  return useMemo(() => getPermissionFromMap(profile, permissionType, teams), [profile, permissionType, teams])
}

export const getPermissionFromMap = (
  profile: ProfileState,
  permissionType: PermissionType,
  teams: TeamData[] | undefined
): EntityId[] => {
  const { user } = profile
  const userTeamIds: TeamId[] = getUserTeamIds(user)

  if (isEmpty(user?.permissionMap?.[permissionType])) return []
  return unique(
    user.permissionMap[permissionType].flatMap((source) => {
      if (source.entity === 'downline') {
        return isTeamId(source.entityId)
          ? getDownlineTeamIds(teams, [source.entityId])
          : getDownlineTeamIds(teams, userTeamIds)
      }
      if (source.entity === 'company') {
        const allTeamIds = getCompanyTeamIds(teams, userTeamIds)
        return [user.companyId, ...allTeamIds]
      }
      if (source.entity === 'withinRestrictions') {
        return getCompanyTeamIds(teams, userTeamIds)
      }
      if (source.entity === 'self') {
        return [user.userId]
      }
      return []
    })
  )
}
const getUserTeamIds = (user: UserData | undefined): TeamId[] => {
  if (!user?.teams) return []
  return unique(user.teams.manages.concat(user.teams.directMemberOf.map(toTeamId)))
}
const getCompanyTeamIds = (teams: TeamData[] | undefined, userTeamIds: TeamId[]): TeamId[] => {
  if (!teams) return []
  return unique(teams.map(toTeamId).concat(userTeamIds))
}
const getDownlineTeamIds = (teams: TeamData[] | undefined, userTeamIds: TeamId[]): TeamId[] => {
  if (!teams) return []
  const downline = teams.filter((team) => team.parentTeamIds.some((id) => userTeamIds.includes(id)))
  return unique(downline.map(toTeamId).concat(userTeamIds))
}
